/** Resume card for the student and freelancer profiles: upload a PDF, open or copy the view-only link, and ask for paid help. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate, inr } from '../../core/format.js';
import { session } from '../../core/session.js';
import { reload } from '../../app/shell.js';
import { banner, ic, tag } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { confirmBox, closeModal, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { payOrder } from '../billing/pay.js';

const MAX_MB = 10;

export async function loadResume(signal) {
  const [mine, help] = await Promise.all([
    api.get('/resumes/mine', { signal }),
    api.get('/resumes/help', { signal }).catch(() => ({ enabled: false, requests: [], price_paise: 0 })),
  ]);
  return { ...mine, help };
}

/** Does this student still need to upload a resume before they can register or pay? (students only; freelancers never block.) */
export async function resumeMissing(signal) {
  try {
    const r = await api.get('/resumes/mine', { signal });
    return Boolean(r.required && !r.resume);
  } catch {
    return false;
  }
}

/** The notice shown before the Register / Pay button, with a way straight to the upload. */
export const resumeNotice = () => html`<div class="mt-s">${banner(html`<b>Upload your resume first.</b> Students add a resume (PDF) before they register or pay. It takes a minute.`, 'wn', 'alert')}<a class="btn wide mt-s" href="/student/profile">${ic('upload')} Upload my resume</a></div>`;

/** Opens in the browser's own PDF viewer with the toolbar hidden: people read it, they do not download it. */
const viewHref = (url) => `${url}#toolbar=0&navpanes=0`;

const STATUS = { awaiting_payment: ['Waiting for payment', 'wn'], paid: ['Paid, our team is building it', 'n'], delivered: ['Delivered', 'ok'] };

function helpBlock(r) {
  const h = r.help;
  if (!h?.enabled) return '';
  const last = h.requests[0];
  const open = last && last.status !== 'delivered';
  return html`<div class="divider"></div><h4 style="margin:0">Need help writing it?</h4>
    <p class="sm mu">Our team builds a clean, job-ready resume from your details. One-time fee <b>${inr(h.price_paise)}</b>${h.gst_percent ? ` plus ${h.gst_percent}% GST` : ''}. The finished PDF replaces the resume on your profile.</p>
    ${last ? html`<p class="sm">Latest request: ${tag(STATUS[last.status]?.[0] || last.status, STATUS[last.status]?.[1] || 'n')} <span class="mu">${fmtDate(last.created_at)}</span></p>` : ''}
    ${open && last.status === 'paid' ? '' : html`<button type="button" class="btn ghost" data-act="resume.help">${ic('spark')} ${last?.status === 'awaiting_payment' ? 'Continue to payment' : 'Get resume building help'}</button>`}`;
}

export function resumeCard(r) {
  const student = session.user?.role === 'student';
  const has = Boolean(r.resume);
  return html`<div class="card"><div class="row between"><h3 style="margin:0">Resume</h3>${student ? tag('Required', has ? 'ok' : 'wn') : tag('Optional', 'n')}</div>
    ${has ? html`<p class="sm mt-s">${ic('file')} <b>${r.resume.filename}</b> <span class="mu">added ${fmtDate(r.resume.created_at)}</span></p>
      <div class="row mt-s"><a class="btn soft sm" href="${viewHref(r.share_url)}" target="_blank" rel="noopener noreferrer">${ic('eye')} View</a>
        <button type="button" class="btn ghost sm" data-act="resume.copy" data-url="${r.share_url}">${ic('link')} Copy link</button>
        ${student ? '' : html`<button type="button" class="btn ghost sm" data-act="resume.remove">Remove</button>`}</div>
      <p class="xs mu mt-s">Anyone with the link can read it in their browser for 30 days. Companies Vignova recommends you to see it as a link too.</p>`
      : (student ? banner('Upload your resume as a PDF. You need it to enrol in a course, ask for a demo or show interest in a role.', 'wn', 'alert')
        : html`<p class="sm mu mt-s">Add a PDF resume if you want clients to see it. You can take it down any time.</p>`)}
    <form data-submit="resume.upload" class="mt-s" novalidate>
      <div class="field" data-req="true" data-type="file"><label for="rs-file">${has ? 'Replace with a new PDF' : 'Choose your resume (PDF)'}</label>
        <input id="rs-file" name="file" type="file" accept=".pdf,application/pdf"><span class="help">PDF only, up to ${MAX_MB} MB. In Word: File, Save as, PDF.</span><span class="err" role="alert">Choose a PDF file.</span></div>
      <button class="btn" type="submit">${has ? 'Replace resume' : 'Upload resume'}</button></form>
    ${helpBlock(r)}</div>`;
}

/** The banner a student sees on their dashboard until a resume is on file. */
export const resumeBanner = (r) => (r?.required && !r.resume
  ? banner(html`<b>Add your resume.</b> Students need a PDF resume to enrol, ask for a demo or apply to roles. <a href="/student/profile">Upload it now</a>`, 'wn', 'alert') : '');

register({
  'resume.upload': (form) => submitForm(form, async () => {
    const file = form.querySelector('input[type=file]').files[0];
    if (!file) throw new Error('Choose a PDF file.');
    if (!/\.pdf$/i.test(file.name)) throw new Error('Please upload your resume as a PDF.');
    if (file.size > MAX_MB * 1024 * 1024) throw new Error(`That file is larger than ${MAX_MB} MB.`);
    const body = new FormData();
    body.append('file', file);
    await api.upload('/resumes/mine', body);
    toast('Resume saved', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
  'resume.copy': async (el) => {
    try {
      await navigator.clipboard.writeText(el.dataset.url);
      toast('Link copied. Anyone with it can read your resume.', 'link');
    } catch {
      toast(el.dataset.url, 'link');
    }
  },
  'resume.remove': async () => {
    if (!(await confirmBox({ title: 'Remove your resume?', text: 'Links you shared will stop working.', label: 'Remove', danger: true }))) return;
    await api.delete('/resumes/mine');
    await reload();
  },
  'resume.help': () => {
    openModal({
      title: 'Resume building help',
      body: html`<form id="resume-help" data-submit="resume.help.send" novalidate>
        ${field('rh-brief', 'Tell us about you', { name: 'brief', rows: 6, minlen: 10, max: 3000, ph: 'Role you are aiming for, your college and degree, projects, internships, skills. Anything to highlight.' })}
        <p class="sm mu">You pay first, then our team builds the resume and uploads it to your profile.</p></form>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="resume-help" class="btn">Continue to payment</button>`,
    });
  },
  'resume.help.send': (form) => submitForm(form, async ({ brief }) => {
    const { order } = await api.post('/resumes/help', { body: { brief } });
    closeModal(false);
    const user = session.user;
    const result = await payOrder(order, { description: 'resume building help', prefill: { name: user.name, email: user.email, contact: user.phone } });
    if (result.status === 'paid') toast('Payment received. We will build your resume.', 'check');
    else if (result.status === 'pending') toast('Thanks. We will start once we confirm your payment.', 'check');
    else if (result.status === 'failed') toast(`${result.message} You have not been charged.`, 'alert');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});
